import React, { useState, useEffect } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import ExecutiveMetrics from './components/ExecutiveMetrics';
import UserManagement from './components/UserManagement';
import LawyerVerification from './components/LawyerVerification';
import SystemMonitoring from './components/SystemMonitoring';
import ProBonoAssignment from './components/ProBonoAssignment';
import AuditLogs from './components/AuditLogs';

const AdminDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const periods = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'verification', label: 'Lawyer Verification', icon: 'ShieldCheck' },
    { id: 'probono', label: 'Pro Bono Cases', icon: 'Heart' },
    { id: 'monitoring', label: 'System Monitoring', icon: 'Activity' },
    { id: 'audit', label: 'Audit Logs', icon: 'FileText' }
  ];

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-KE', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-KE', { 
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      
      <div className="md:ml-64 transition-layout">
        {/* Header */}
        <header className="bg-surface border-b border-border px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-error rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-text-primary">Admin Dashboard</h1>
                  <p className="text-text-secondary">
                    {formatDate(currentTime)} • {formatTime(currentTime)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Period Selector */}
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e?.target?.value)}
                className="bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {periods?.map((period) => (
                  <option key={period?.value} value={period?.value}>
                    {period?.label}
                  </option>
                ))}
              </select>

              {/* Notifications */}
              <button className="relative p-2 text-text-secondary hover:text-text-primary transition-smooth">
                <Icon name="Bell" size={20} />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-error rounded-full"></span>
              </button>
              
              {/* Profile */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-error rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-text-primary">Administrator</p>
                  <p className="text-xs text-text-secondary">Full Access</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex overflow-x-auto space-x-2">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-primary text-white' :'bg-secondary-50 text-text-secondary hover:bg-secondary-100'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </header>

        {/* Main Content */}
        <main className="p-6">
          {activeTab === 'overview' && (
            <>
              <ExecutiveMetrics selectedPeriod={selectedPeriod} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <SystemMonitoring />
                <div className="space-y-6">
                  <LawyerVerification compact />
                  <ProBonoAssignment compact />
                </div>
              </div>
            </>
          )}

          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'verification' && <LawyerVerification />}
          {activeTab === 'probono' && <ProBonoAssignment />}
          {activeTab === 'monitoring' && <SystemMonitoring detailed />}
          {activeTab === 'audit' && <AuditLogs />}

          {/* Mobile Alert */}
          <div className="md:hidden bg-warning-50 border border-warning-200 rounded-lg p-4 mt-6">
            <div className="flex items-center space-x-3">
              <Icon name="Smartphone" size={20} className="text-warning-600" />
              <div>
                <h3 className="text-sm font-medium text-warning-700">Mobile Access</h3>
                <p className="text-xs text-warning-600 mt-1">
                  Critical alerts and user approval tasks optimized for mobile monitoring
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;